import axios, { AxiosInstance, AxiosResponse } from "axios";
import { WarrantyTicketsResponse } from "../../@types/DRO/WarrantyTicketsResponse";
import { WarrantyActionBody } from "../../@types/DTO/WarrantyActionBody";
import { ERP_BASE_URL } from "../../config/apiConfig";

const getHeaderValue = (headerValue: unknown, fallback = "") => {
  if (typeof headerValue === "string") {
    return headerValue;
  }

  if (Array.isArray(headerValue)) {
    return headerValue.join(", ");
  }

  return fallback;
};

type BulkUploadWarrantyTicketsResult = {
  success: boolean;
  message?: string;
  hasErrorFile?: boolean;
};

export class WarrantyTicketsApi {
  public static api = new WarrantyTicketsApi();
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: ERP_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  private getToken() {
    return localStorage.getItem("token");
  }

  async getWarrantyTickets(
    page: number = 0,
    pageSize: number = 10,
    search: string = "",
    status: string = "",
    fromDate: string = "",
    toDate: string = ""
  ): Promise<WarrantyTicketsResponse> {
    try {
      const response: AxiosResponse<WarrantyTicketsResponse> =
        await this.instance.get("/erp/api/v1/vendor/warranty/tickets", {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            page,
            pageSize,
            ...(search ? { search } : {}),
            ...(status && status !== "All" ? { status } : {}),
            ...(fromDate ? { fromDate } : {}),
            ...(toDate ? { toDate } : {}),
          },
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching warranty tickets", error);
      throw error;
    }
  }

  async getWarrantyTicketsHistory(
    page: number = 0,
    pageSize: number = 10,
    search: string = "",
    status: string = "",
    fromDate: string = "",
    toDate: string = ""
  ): Promise<WarrantyTicketsResponse> {
    try {
      const response: AxiosResponse<WarrantyTicketsResponse> =
        await this.instance.get("/erp/api/v1/vendor/warranty/tickets/history", {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            page,
            pageSize,
            ...(search ? { search } : {}),
            ...(status && status !== "All" ? { status } : {}),
            ...(fromDate ? { fromDate } : {}),
            ...(toDate ? { toDate } : {}),
          },
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching warranty tickets history", error);
      throw error;
    }
  }

  async bulkUploadWarrantyTickets(
    file: File
  ): Promise<BulkUploadWarrantyTicketsResult> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axios.post(
      `${ERP_BASE_URL}/erp/api/v1/vendor/warranty/ticket/bulk/upload`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
        responseType: "blob",
      }
    );

    const contentType = getHeaderValue(response.headers["content-type"]);

    if (
      contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") ||
      contentType.includes("application/vnd.ms-excel") ||
      contentType.includes("application/octet-stream")
    ) {
      this.downloadFile(response, "Error Sheet.xlsx", true);
      return {
        success: false,
        hasErrorFile: true,
        message:
          "Excel response downloaded. Please review the file for row-level details.",
      };
    }

    const text = await response.data.text();

    try {
      const jsonData = JSON.parse(text);
      return {
        success: true,
        message:
          jsonData?.message ||
          jsonData?.data ||
          "File uploaded successfully",
      };
    } catch {
      return {
        success: true,
        message: "File uploaded successfully",
      };
    }
  }

  async warrantyTicketAction(
    body: WarrantyActionBody
  ): Promise<{ code: number; message: string; data: {} }> {
    const response = await this.instance.post(
      "/erp/api/v1/vendor/warranty/ticket/action",
      body,
      {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      }
    );
    return response.data;
  }

  async getHubList(): Promise<{ hubId: number; hubName: string }[]> {
    const response = await this.instance.get("/erp/api/v1/vendor/hub/list", {
      headers: { Authorization: `Bearer ${this.getToken()}` },
    });
    return response.data?.data ?? [];
  }

  async getRejectionReasons(): Promise<{ id: number; rejectionReason: string }[]> {
    const response = await this.instance.get("/erp/api/v1/vendor/reject/reason/list", {
      headers: { Authorization: `Bearer ${this.getToken()}` },
    });
    return response.data?.data?.rejectionReasonList ?? [];
  }

  async downloadTemplateSheet(): Promise<Blob> {
    const response = await this.instance.get(
      "/erp/api/v1/vendor/download/sheet",
      {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
        responseType: "blob",
      }
    );
    return response.data;
  }

  private downloadFile(
    response: AxiosResponse<Blob>,
    defaultFileName: string,
    useProvidedName = false
  ) {
    const contentType = getHeaderValue(
      response.headers["content-type"],
      "application/octet-stream"
    );
    const contentDisposition = getHeaderValue(
      response.headers["content-disposition"]
    );

    let fileName = useProvidedName
      ? defaultFileName
      : `${defaultFileName}-${new Date().toISOString().split("T")[0]}.xlsx`;

    if (!useProvidedName && contentDisposition) {
      const filenameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      );

      if (filenameMatch?.[1]) {
        fileName = filenameMatch[1].replace(/['"]/g, "");
      } else {
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
        if (utf8Match?.[1]) {
          fileName = decodeURIComponent(utf8Match[1]);
        }
      }
    }

    const blob = new Blob([response.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    window.URL.revokeObjectURL(url);
  }
}


